import { Box, Button, Stack, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ReadyState } from 'react-use-websocket';
import * as Sentry from '@sentry/react';

import {
  ConnectorStatus,
  StationStatus,
} from '../../common/enums/connectorStatus';
import { Loading } from '../../components/common/Loading/Loading';
import { useMyStationPriceQuery } from '../../hooks/usePremiumQueries';
import { useUser, useUserActions } from '../../hooks/useUser';
import { useUserStatus } from '../../hooks/useUserStatus';
import { useWebSocketContext } from '../../hooks/useWebsocketContext';
import { OcppService } from '../../services/ocpp/OcppService';
import { parseApiError } from '../../utils/parseApiError';
import { isRemoteStartRejectedMessage } from '../../common/interfaces/socketMessageTypes';
import { UserSessionResponse } from '../../services/ocpp/types';
import { ChargingBar } from './components/ChargingBar/ChargingBar';
import { IdleStatus } from './components/IdleStatus/IdleStatus';
import { ChosenConnector } from './components/ChosenConnector/ChosenConnector';
import { StationPageHeader } from './components/StationPageHeader/StationPageHeader';
import { InputValueBlock } from './components/InputValueBlock/InputValueBlock';
import { ConnectorPicker } from './components/ConnectorPicker/ConnectorPicker';
import { InsertTheConnector } from './components/InsertTheConnector/InsertTheConnector';
import { ConnectorInUse } from './components/ConnectorInUse/ConnectorInUse';
import { PayAmountBlock } from './components/PayAmountBlock/PayAmountBlock';
import { Connector, StationTypes, ToPay } from './types';
import { localStorageService } from '../../services/localStorageService/localStorageService';
import { ROUTES } from '../../constants/routes';
import { StationInUse } from './components/StationInUse/StationInUse';
import { StationButton } from './components/StationButton/StationButton';
import { useTranslation } from 'react-i18next';
import { SessionType } from '../../components/common/Card/UserSessionCard/types';
import { useChargingSessionPolling } from '../../hooks/useChargingSessionPolling';
import { useConnectorStatusPolling } from '../../hooks/useConnectorStatusPolling';
import { useIdlePreview } from '../../hooks/useIdlePreview';
import { TelegramNotifyPrompt } from '../../components/features/TelegramNotifyPrompt/TelegramNotifyPrompt';
import { ConnectionBanner } from './components/ConnectionBanner/ConnectionBanner';
import { resolveChosenConnector } from './resolveChosenConnector';
import { sessionForConnector } from '../../utils/activeSessions';
import { resolveScreenSession } from './resolveScreenSession';

const ocppService = new OcppService();

/**
 * Floor for the manual refresh spinner. The snapshot usually lands well inside
 * this, and a spinner that blinks once reads as a tap that did nothing.
 */
const MIN_REFRESH_SPIN_MS = 600;

/**
 * The bays a driver can be sent to. Connector 0 is the charge point itself in
 * OCPP, not a socket, so it is never one of them. Shared because the count has
 * to agree everywhere: it decides whether the picker renders, whether the back
 * arrow is offered, and — through `resolveScreenSession` — whether a URL with
 * no connector in it names a bay at all.
 */
const usableConnectors = (connectors: Connector[]): Connector[] =>
  connectors.filter((c) => c.connectorId > 0);

export const Station = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [station, setStation] = useState<StationTypes | null>(null);
  const [chosenConnector, setChosenConnector] = useState<
    Connector | null | false
  >(false);
  const [progress, setProgress] = useState(0);
  // Every connector at this station the account is charging on. The picker
  // needs all of them: it keeps a Charging connector selectable only when the
  // charge on it is the driver's own, and an account can hold both bays.
  const [myChargingConnectorIds, setMyChargingConnectorIds] = useState<number[]>(
    [],
  );
  const [chargingError, setChargingError] = useState<string | null>(null);
  const [toPay, setToPay] = useState<ToPay | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [activeChargingSession, setActiveChargingSession] = useState<
    SessionType | undefined | null
  >(null);
  const [chargingMeta, setChargingMeta] = useState<{
    powerKw?: number;
    startedAt?: string;
  }>({});
  const [refreshing, setRefreshing] = useState(false);

  const { user } = useUser();
  const userId = user?.id || localStorageService().getUserId();
  const { isUserPhoneConfirmed } = useUserStatus();
  const { id, connectorId: connectorIdParam } = useParams();
  // Premium is granted per organization and station, so the price is asked
  // for this station rather than read off the user.
  const { data: myPrice } = useMyStationPriceQuery(id, Boolean(userId));
  const userPremiumPrice = myPrice?.isPremium ? myPrice.pricePerKwh : null;
  // Read by the reconcile callbacks, which must not be rebuilt every time the
  // driver moves between bays. `null` means the picker is open.
  const chosenConnectorRef = useRef(chosenConnector);
  chosenConnectorRef.current = chosenConnector;
  /**
   * The bay this screen pressed Start on, if any.
   *
   * A charge is the driver's own the instant they start it, but the server
   * only lists it once it is CHARGING — `/sessions/my/active` filters on that
   * status, so a session still in PREPARE is in no list the page can read.
   * Between the charger reporting Charging over the socket and the next REST
   * round-trip, the ownership check below would otherwise call the driver's
   * own brand-new charge a stranger's and hide it behind "connector in use".
   */
  const startedBayRef = useRef<number | null>(null);

  // The driver's own live charges at this station: the last REST snapshot's
  // list plus the session this screen is showing. A charge that has just
  // started arrives over the socket rather than in a fresh snapshot, so the
  // snapshot list alone would still call their own bay somebody else's and
  // the picker would grey it out.
  const myConnectorIds = activeChargingSession
    ? [
        ...new Set([
          ...myChargingConnectorIds,
          +activeChargingSession.connectorId,
        ]),
      ]
    : myChargingConnectorIds;
  // Read by `selectConnector`, which must stay stable: an effect depends on it.
  const myConnectorIdsRef = useRef<number[]>(myConnectorIds);
  myConnectorIdsRef.current = myConnectorIds;
  const { onChangeUserBalance } = useUserActions();
  const navigate = useNavigate();
  const {
    connectWebSocket,
    lastMessage: socketMessage,
    readyState,
    reconnectedAt,
  } = useWebSocketContext();
  const isWsConnected = readyState === ReadyState.OPEN;
  const { t } = useTranslation();

  // `connectorId` is passed explicitly by the picker, which calls this the
  // moment a connector is chosen — before the URL it navigates to has landed.
  const setChargingProcess = useCallback(
    async (connectorId?: string | number) => {
      try {
        if (!userId || userId === 'undefined') return;
        // The account may be charging on the other connector of this same
        // station, so take the charge running on the connector this screen is
        // about rather than whichever one comes back first.
        const sessions = await ocppService.getActiveSessions();
        const target =
          connectorId ??
          (chosenConnector ? chosenConnector.connectorId : connectorIdParam);
        const response = sessionForConnector(sessions, id, target);
        // Once charging stops this station reports no active session. Keep the
        // last known values so the completed bar and the idle tracker on the
        // Finishing screen don't reset to zero.
        if (!response?.sessionId) return;
        setProgress(response.percent);
        setChargingMeta({
          powerKw: response.powerKw,
          startedAt: response.startedAt,
        });
      } catch (error) {
        Sentry.captureException({
          message: '[ocppService][getActiveSessions]',
          error,
        });
      }
    },
    [userId, id, chosenConnector, connectorIdParam],
  );

  // Fetches station + active-session truth from REST in one shot and normalizes
  // the result so callers don't have to repeat the lastSession-vs-progress
  // reconciliation logic.
  const loadStationSnapshot = useCallback(
    async (stationId: string, connectorFromUrl?: string) => {
      const [station, lastSession, activeSessions] = await Promise.all([
        ocppService.getStation(stationId),
        userId ? ocppService.getLastUserSession(stationId) : Promise.resolve(null),
        userId
          ? ocppService.getActiveSessions().catch((): UserSessionResponse[] => [])
          : Promise.resolve<UserSessionResponse[]>([]),
      ]);

      return {
        station,
        ...resolveScreenSession(
          stationId,
          connectorFromUrl,
          lastSession,
          activeSessions,
          usableConnectors(station.connectors).length,
        ),
      };
    },
    [userId],
  );

  // Apply an active session from a snapshot to component state. Returns true
  // if there was an active session to apply.
  const applyActiveSession = useCallback(
    (snap: Awaited<ReturnType<typeof loadStationSnapshot>>) => {
      if (!snap.activeConnectorId) return false;
      setProgress(snap.progressPercent);
      if (snap.activeSession) setActiveChargingSession(snap.activeSession);
      const activeConnector = snap.station.connectors.find(
        (c) => c.connectorId === snap.activeConnectorId,
      );
      if (activeConnector) setChosenConnector(activeConnector);
      return true;
    },
    [],
  );

  const fetchData = useCallback(
    async (id: string, connectorFromUrl?: string) => {
      try {
        setLoading(true);
        const snap = await loadStationSnapshot(id, connectorFromUrl);
        setStation(snap.station);
        setMyChargingConnectorIds(snap.myConnectorIds);

        if (applyActiveSession(snap)) {
          // Active charging — clear any pre-filled kWh from a prior top-up flow
          localStorageService().removePrefilledKwt();
        } else if (connectorFromUrl) {
          // Explicit connector in URL (picker selection or payment redirect)
          const target = snap.station.connectors.find(
            (c) => c.connectorId === Number(connectorFromUrl),
          );
          setChosenConnector(target || null);
        } else {
          // No URL hint — auto-select the only usable connector if there is one
          const usable = usableConnectors(snap.station.connectors);
          setChosenConnector(usable.length === 1 ? usable[0] : null);
        }

        onChangeUserBalance();
        connectWebSocket(id, connectorFromUrl ? Number(connectorFromUrl) : null);
        setTimeout(() => setLoading(false), 1000);
        // Handed back so a caller can tell what the server actually said —
        // `onButtonClick` needs to know whether the start it just sent took.
        return snap;
      } catch (error) {
        Sentry.captureException({
          message: '[ocppService][getStation]',
          error,
        });
        return null;
      }
    },
    [loadStationSnapshot, applyActiveSession, connectWebSocket, onChangeUserBalance],
  );

  // Re-read the chosen connector's status from a fresh snapshot. `station` and
  // `chosenConnector` are separate state, and `applyActiveSession` only writes
  // the latter when a session is running — so without this a re-sync updates
  // the station underneath a screen that never moves.
  const syncChosenConnector = useCallback(
    (snap: Awaited<ReturnType<typeof loadStationSnapshot>>) => {
      setChosenConnector((prev) =>
        resolveChosenConnector(prev, snap.station.connectors),
      );
    },
    [],
  );

  /**
   * Move the screen to another bay of this station.
   *
   * Shared by the in-page picker and by the URL-follow effect below, because
   * both mean the same thing: the charge the last bay was showing must not
   * come along, and an error raised against that bay is not this one's.
   */
  const selectConnector = useCallback(
    (connector: Connector) => {
      setChosenConnector(connector);
      setChargingError(null);
      startedBayRef.current = null;
      if (myConnectorIdsRef.current.includes(connector.connectorId)) {
        // Reopening their own charge — reload its live numbers.
        setChargingProcess(connector.connectorId);
      } else {
        // A different bay. Drop the session the last one was showing, or its
        // charging bar and delivered kWh follow the driver over to a connector
        // that charge isn't on.
        setActiveChargingSession(null);
      }
    },
    [setChargingProcess],
  );

  // Silent re-sync after a WS reconnect. Reconciles state against REST so the
  // UI recovers even if the OCPP server replays a stale `Available` frame or
  // drops status messages while we were offline. Never touches `loading` or
  // re-opens the socket — only updates state when there is an active session.
  const rehydrateFromServer = useCallback(
    async (stationId: string, connectorFromUrl?: string) => {
      try {
        const snap = await loadStationSnapshot(stationId, connectorFromUrl);
        setStation(snap.station);
        setMyChargingConnectorIds(snap.myConnectorIds);
        // The picker is a deliberate step back from one bay to the station, so
        // a resync must not adopt a running charge here: that dragged the
        // driver back to their own charge every beat and the free bay stayed
        // out of reach. The statuses above still refresh, which is all the
        // picker is polling for.
        if (chosenConnectorRef.current === null) return;
        // applyActiveSession already picks the connector when a session is
        // running; only the no-session case needs the status synced by hand.
        if (!applyActiveSession(snap)) syncChosenConnector(snap);
      } catch (error) {
        Sentry.captureException({
          message: '[ocppService][rehydrateFromServer]',
          error,
        });
      }
    },
    [loadStationSnapshot, applyActiveSession, syncChosenConnector],
  );

  useEffect(() => {
    if (!reconnectedAt || !id) return;
    rehydrateFromServer(id, connectorIdParam);
  }, [reconnectedAt, id, connectorIdParam, rehydrateFromServer]);

  // Driver-initiated version of the same reconcile. The screen is driven by a
  // socket that can drop or replay a stale `Available`, and the charger itself
  // can be slow to report — so the driver needs a way to ask rather than wait.
  // Deliberately never touches `loading`: a full-page spinner here would throw
  // away the charging bar and flash the whole shell.
  const handleRefresh = useCallback(async () => {
    if (!id || refreshing) return;
    setRefreshing(true);
    const startedAt = Date.now();
    try {
      await rehydrateFromServer(id, connectorIdParam);
      onChangeUserBalance();
      // A dead socket is the likeliest reason the page went stale in the first
      // place, so reopen it rather than leaving the driver to refresh again.
      if (!isWsConnected) {
        connectWebSocket(id, chosenConnector ? chosenConnector.connectorId : null);
      }
    } finally {
      const elapsed = Date.now() - startedAt;
      const settle = Math.max(0, MIN_REFRESH_SPIN_MS - elapsed);
      if (settle) await new Promise((resolve) => setTimeout(resolve, settle));
      setRefreshing(false);
    }
  }, [
    id,
    connectorIdParam,
    refreshing,
    rehydrateFromServer,
    onChangeUserBalance,
    isWsConnected,
    connectWebSocket,
    chosenConnector,
  ]);

  const checkDisableButton = () => {
    if (disableButton) {
      return true;
    }

    if (
      chosenConnector &&
      chosenConnector?.status !== ConnectorStatus.Preparing
    ) {
      return true;
    }
    if (!toPay) {
      return true;
    }
    if (
      activeChargingSession &&
      chosenConnector &&
      +activeChargingSession.connectorId === +chosenConnector?.connectorId
    ) {
      return false;
    }
    return false;
  };

  const onButtonClick = async () => {
    setChargingError('');
    setDisableButton(true);
    setLoading(true);
    try {
      if (toPay?.amount === 0 && chosenConnector) {
        // User has enough balance - start charging directly
        // Clear pre-filled kWh after successful start
        localStorageService().removePrefilledKwt();
        await ocppService.startTransactionWithoutPayments({
          // The bay on screen, never the one an earlier charge of theirs runs
          // on: preferring the active session's connector aimed a second
          // start back at the busy bay, which the backend then refused.
          connectorId: Number(chosenConnector.connectorId),
          idTag: station?.idTag as string,
          kwt: toPay?.kwt,
        });
        // Keep the spinner up and the button disabled until fetchData resolves —
        // otherwise the user can double-click and the page flickers from
        // Preparing → Loading → Charging.
        startedBayRef.current = Number(chosenConnector.connectorId);
        const snap = id ? await fetchData(id, connectorIdParam) : null;
        // ...but fetchData resolving does not mean the charger has started.
        // The connector can still read Preparing for a beat while the charge
        // is already live, and giving the button back on that let a second
        // press through — which the backend, checking the connector's real
        // status, answered with 409 "connector already in use" for the charge
        // the first press had just started. Re-enable only when no charge of
        // theirs is running on this bay, which is also the case the retry is
        // there for: a charger that ignored the RemoteStart entirely.
        if (snap?.activeConnectorId !== Number(chosenConnector.connectorId)) {
          setDisableButton(false);
        }
      } else if (chosenConnector) {
        // Save kWh value before payment redirect
        if (inputValue) {
          localStorageService().setPrefilledKwt(inputValue);
        }
        // User needs to top up balance - create invoice for balance top-up
        await ocppService.createInvoice({
          idTag: station!.idTag,
          connectorId: chosenConnector?.connectorId,
          kwt: toPay?.kwt as number,
        });
        // Button stays disabled - payment redirect will happen
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        setDisableButton(false);
        setChargingError(parseApiError(error, '').message);
        setLoading(false);
      }
    }
  };

  const stopTransaction = async () => {
    const isConfirmed = window.confirm(
      t('orderPage.confirmations.stopSession'),
    );

    if (!isConfirmed) {
      return;
    }

    if (chosenConnector) {
      setLoading(true);
      setChargingError(null);
      try {
        await ocppService.stopTransaction({
          idTag: station?.idTag as string,
          connectorId: chosenConnector?.connectorId,
        });
        localStorageService().removeMeterValue(chosenConnector?.connectorId);

        // Don't redirect — the car is still plugged in, so the connector enters
        // the idle (Finishing) window. Stay on the charging page and show the
        // completed bar plus the idle tracker; the user leaves via "Done" or by
        // unplugging (the WS Available frame then resets the view).
        setChosenConnector((prev) =>
          prev ? { ...prev, status: ConnectorStatus.Finishing } : prev,
        );
      } catch (error) {
        // Now that the endpoint checks ownership, a stranger gets a 403 here
        // instead of always succeeding — without a catch that rejection was
        // unhandled and the spinner never came back down.
        setChargingError(parseApiError(error, t('orderPage.stopFailed')).message);
      } finally {
        setLoading(false);
      }
    }
  };

  const chargedEnergyKwh = useMemo(() => {
    if (!activeChargingSession) {
      return null;
    }

    const meterStart = activeChargingSession.meterStart || 0;
    const meterValue = activeChargingSession.meterValue || 0;
    const charged = (meterValue - meterStart) / 1000;

    if (!Number.isFinite(charged)) {
      return null;
    }

    return Math.max(0, charged);
  }, [activeChargingSession]);

  // The car can sit plugged in after charging stops (connector reports
  // Finishing, or SuspendedEV when the car itself stopped drawing); for
  // stations that bill downtime, surface the live idle status. The backend
  // decides whether the clock is really running (`preview.isIdle`).
  const downtimeBillingEnabled = (station?.downtimePricePerMinute ?? 0) > 0;
  const isIdleWindow =
    !!chosenConnector &&
    (chosenConnector.status === ConnectorStatus.Finishing ||
      chosenConnector.status === ConnectorStatus.Suspended_EV) &&
    downtimeBillingEnabled;
  const { preview: idlePreview } = useIdlePreview(
    activeChargingSession?.sessionId ?? null,
    isIdleWindow,
  );

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    if (!isUserPhoneConfirmed) {
      // null = still checking → keep loading spinner
      // false = not confirmed → stop loading, redirect handled below
      if (isUserPhoneConfirmed === false) {
        setLoading(false);
      }
      return;
    }

    const connId = connectorIdParam ? Number(connectorIdParam) : undefined;
    const meterValue = localStorageService().getMeterValue(connId);
    if (meterValue) {
      setProgress(Number(meterValue));
    }

    // Restore pre-filled kWh value after payment redirect
    const prefilledKwt = localStorageService().getPrefilledKwt();
    if (prefilledKwt) {
      setInputValue(prefilledKwt);
      // Trigger calculation for pre-filled value
      const kwt = parseInt(prefilledKwt);
      if (kwt > 0) {
        ocppService.calculate(id, kwt).then((response) => {
          setToPay({
            kwt: response.kwt,
            amount: +response.amount,
            serviceFee: +response.serviceFee || 0,
            time: +response.time,
            balanceDeduction: +response?.balanceDeduction || 0,
          });
        }).catch((error) => {
          Sentry.captureException({
            message: '[ocppService][calculate][prefilled]',
            error,
          });
        });
      }
    }

    fetchData(id, connectorIdParam);
    // `connectorIdParam` is read but deliberately not a dependency: this is the
    // station's first load, and re-running it on every bay change would mean a
    // full snapshot and a full-page spinner each time the driver taps a
    // connector in the picker. Later changes to the bay in the URL are the
    // URL-follow effect's job, which switches bays out of the station already
    // in hand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isUserPhoneConfirmed, fetchData]);

  const activeConnectorId: number | null = chosenConnector
    ? chosenConnector.connectorId
    : connectorIdParam
      ? Number(connectorIdParam)
      : null;

  // Handle real-time connector status updates via WebSocket
  useEffect(() => {
    if (!socketMessage) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const connStatus = (socketMessage as any)?.connectorStatus;
    if (!connStatus) return;

    const msgConnectorId = +connStatus.connectorId;

    // Update connector status in station data for real-time picker updates.
    // Diff inside the updater and bail out when nothing changed — otherwise
    // the fresh array/object references would re-trigger this effect forever.
    setStation((prev) => {
      if (!prev) return prev;
      let changed = false;
      const updatedConnectors = prev.connectors.map((c) => {
        if (c.connectorId === msgConnectorId && c.status !== connStatus.status) {
          changed = true;
          return { ...c, status: connStatus.status };
        }
        return c;
      });
      return changed ? { ...prev, connectors: updatedConnectors } : prev;
    });

    // If user has a connector selected and it gets a Preparing update, reflect it
    if (connStatus.status === ConnectorStatus.Preparing) {
      if (activeConnectorId && activeConnectorId === msgConnectorId) {
        setChosenConnector({ connectorId: msgConnectorId, status: ConnectorStatus.Preparing });
      }
      return;
    }

    // Handle Available — reset connector only when transitioning from a non-available
    // state (e.g. Preparing → Available means the session was cancelled).
    // Do NOT reset if the connector was already Available (routine status refresh)
    // or if fetchData hasn't run yet (chosenConnector === false), because that would
    // strip the connectorId from the URL before the user's selection is established.
    if (connStatus.status === ConnectorStatus.Available) {
      // Server-of-record check: if the user has an open transaction on this
      // connector, ignore an `Available` frame. OCPP can emit one right after
      // a WS reconnect even though charging is still ongoing in our DB.
      if (
        activeChargingSession &&
        +activeChargingSession.connectorId === msgConnectorId
      ) {
        return;
      }
      if (!activeConnectorId || activeConnectorId === msgConnectorId) {
        setChosenConnector((prev) => {
          if (prev === false) return prev; // fetchData not yet run — don't touch
          const prevConnector = prev as Connector | null;
          if (prevConnector?.status === ConnectorStatus.Available) return prev; // already available, no change
          return null; // was Preparing/Charging/Finishing → now free, reset selection
        });
      }
      return;
    }

    // Charging/Finishing — only process if it matches the user's own connector.
    // Without this guard, a picker-view client (no activeConnectorId) would adopt
    // another user's connector when its status changes, surfacing their session UI.
    if (!activeConnectorId || activeConnectorId !== msgConnectorId) {
      return;
    }

    if (
      connStatus.status === ConnectorStatus.Charging ||
      connStatus.status === ConnectorStatus.Finishing
    ) {
      onChangeUserBalance();
    }

    setChosenConnector((prev) =>
      prev
        ? { ...prev, connectorId: msgConnectorId, status: connStatus.status }
        : { connectorId: msgConnectorId, status: connStatus.status },
    );
  }, [socketMessage, activeConnectorId, activeChargingSession?.connectorId]);

  /**
   * A start that raced the charger — a second press, or a retry after a frame
   * went missing — comes back 409 "connector already in use" while the charge
   * it collided with is running perfectly well. Nothing cleared that, so it sat
   * in red under the Stop button for the rest of the session.
   *
   * Clear it the moment the bay actually reaches Charging. A failed *stop*
   * leaves the status alone, so the error this surface was built for survives.
   */
  const lastStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const status = chosenConnector ? (chosenConnector.status as string) : undefined;
    const wasCharging = lastStatusRef.current === ConnectorStatus.Charging;
    lastStatusRef.current = status;
    if (status === ConnectorStatus.Charging && !wasCharging) {
      setChargingError(null);
    }
  }, [chosenConnector]);

  // The charger refused our RemoteStartTransaction (its own transaction is
  // still running, say). The server has already closed the PREPARE session;
  // without this the page kept its spinner up waiting for a StartTransaction
  // that never came, and the user's only move was to press Start again.
  useEffect(() => {
    if (!isRemoteStartRejectedMessage(socketMessage)) return;
    const rejectedConnectorId = +socketMessage.remoteStartRejected.connectorId;
    if (activeConnectorId && activeConnectorId !== rejectedConnectorId) return;

    setChargingError(t('orderPage.remoteStartRejected'));
    setDisableButton(false);
    setLoading(false);
  }, [socketMessage, activeConnectorId, t]);

  // Use primitive connectorId to avoid re-running on every object reference change
  // chosenConnector === false means initial state (not yet loaded) — skip URL sync
  // to avoid stripping connectorId from URL before fetchData reads it
  const chosenConnectorId = chosenConnector ? chosenConnector.connectorId : null;
  const connectorLoaded = chosenConnector !== false;
  const urlBay = connectorIdParam ? Number(connectorIdParam) : null;
  const urlNamesRealBay =
    urlBay !== null &&
    !!station &&
    usableConnectors(station.connectors).some((c) => c.connectorId === urlBay);

  // `navigate` is rebuilt whenever the location changes, so listing it as a
  // dependency made the effect below fire on *every* URL change — including
  // one from outside this page, which it then promptly overwrote. Its job is
  // to mirror this screen into the URL, so it reacts to the screen.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  /**
   * Keep the URL in step with the bay on screen, so every bay has its own link.
   *
   * It fills in a connector the URL is missing and strips one the screen no
   * longer has, but it leaves a URL that already names a real bay alone. That
   * last part is what lets something outside the page — the charging tab's
   * sheet, moving between two charges of the same station — point this screen
   * somewhere: it used to rewrite the URL straight back to the bay already
   * shown, so the sheet appeared to do nothing at all.
   */
  useEffect(() => {
    if (!id || !connectorLoaded) return;
    if (chosenConnectorId && !urlNamesRealBay) {
      navigateRef.current(
        `/${ROUTES.CABINET}/${ROUTES.CAB_STATION}/${id}/${chosenConnectorId}`,
        { replace: true },
      );
    } else if (!chosenConnectorId && urlBay !== null) {
      navigateRef.current(
        `/${ROUTES.CABINET}/${ROUTES.CAB_STATION}/${id}`,
        { replace: true },
      );
    }
  }, [chosenConnectorId, connectorLoaded, id, urlBay, urlNamesRealBay]);

  /**
   * ...and the other direction: when the URL names a different bay, follow it.
   *
   * The route is `station/:id/:connectorId?`, so moving between two bays of one
   * station never unmounts this component and the mount-time read of the URL
   * never runs again. Without this the charging sheet switched the address bar
   * and nothing else.
   *
   * Only ever acts when a bay is already on screen: `null` means the picker is
   * open, which is a deliberate step back that neither the URL nor a sheet
   * should undo.
   */
  useEffect(() => {
    if (!station || !chosenConnector) return;
    if (urlBay === null || urlBay === chosenConnector.connectorId) return;
    const target = usableConnectors(station.connectors).find(
      (c) => c.connectorId === urlBay,
    );
    // A bay this station does not have: leave the screen where it is. The sync
    // effect above puts the URL right, and neither one loops.
    if (target) selectConnector(target);
  }, [urlBay, station, chosenConnector, selectConnector]);

  useChargingSessionPolling({
    stationId: id,
    userId,
    chosenConnector,
    isWsConnected,
    onRefreshProgress: setChargingProcess,
    onUpdateActiveSession: setActiveChargingSession,
  });

  // Waiting-to-plug-in screens (the picker, "insert the connector", and the kWh
  // form) move only when a `connectorStatus` frame arrives, and that socket can
  // miss one. It shows up worst on a station with two or more connectors: the
  // driver plugs in and the page keeps asking them to plug in. Re-read the
  // statuses from REST on a short beat so the screen advances on its own.
  useConnectorStatusPolling({
    stationId: id,
    chosenConnector,
    onResync: rehydrateFromServer,
  });

  // On completion just clean up the cached meter value. Don't redirect — the
  // connector enters the idle (Finishing) window and we keep the user on the
  // charging page to show the completed bar and the idle tracker.
  useEffect(() => {
    if (progress === 100) {
      localStorageService().removeMeterValue(activeConnectorId ?? undefined);
    }
  }, [progress, activeConnectorId]);

  // Entering the idle (Finishing) window: the live progress endpoint no longer
  // reports the session, so pull the final meter values from the last session
  // and recompute the percentage from them. This keeps the completed bar from
  // freezing at the last in-progress poll and keeps % consistent with energy.
  const finishingStatus =
    chosenConnector && chosenConnector.status === ConnectorStatus.Finishing;
  useEffect(() => {
    if (!finishingStatus || !id) return;
    ocppService
      .getLastUserSession(id)
      .then((last) => {
        if (!last?.sessionId) return;
        setActiveChargingSession(last);
        const delivered = (last.meterValue ?? 0) - (last.meterStart ?? 0);
        if (last.meterStop && last.meterStop > 0) {
          const pct = (delivered / last.meterStop) * 100;
          setProgress(Math.min(100, Math.max(0, Math.round(pct))));
        }
      })
      .catch((error) => {
        Sentry.captureException({
          message: '[ocppService][getLastUserSession][finishing]',
          error,
        });
      });
  }, [finishingStatus, id]);

  // Auto-refresh calculation every 10 seconds to update balance deduction after top-up
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;

  useEffect(() => {
    // Only run interval when connector is in Preparing state and user has entered kWh
    if (
      !id ||
      !chosenConnector ||
      chosenConnector.status !== ConnectorStatus.Preparing ||
      !inputValueRef.current
    ) {
      return;
    }

    const refreshCalculation = async () => {
      try {
        // Refresh user balance first
        onChangeUserBalance();

        // Then recalculate with current kWh value
        const kwt = parseInt(inputValueRef.current);
        if (kwt > 0) {
          const response = await ocppService.calculate(id, kwt);
          setToPay({
            kwt: response.kwt,
            amount: +response.amount,
            serviceFee: +response.serviceFee || 0,
            time: +response.time,
            balanceDeduction: +response?.balanceDeduction || 0,
          });
        }
      } catch (error) {
        Sentry.captureException({
          message: '[ocppService][calculate][interval]',
          error,
        });
      }
    };

    const intervalId = setInterval(refreshCalculation, 3000);

    return () => clearInterval(intervalId);
  }, [id, chosenConnector, onChangeUserBalance]);

  if (loading) return <Loading />;

  const stationAddress = station?.address?.trim();
  // Going back means "pick a different connector", so it is offered whenever
  // there is a list to go back to. It used to be withheld while any charge of
  // the driver's own ran at this station, which left a two-bay station with no
  // route to its free connector at all — the picker is non-destructive and
  // leads straight back to a running charge, so there is nothing to protect.
  const canChangeConnector =
    !!chosenConnector &&
    !!station &&
    usableConnectors(station.connectors).length > 1;

  // After the initial load, any non-OPEN readyState means the WS dropped and
  // is trying to come back. Surface this explicitly so the user doesn't read
  // a stale connector status as "station unavailable".
  const reconnectingBanner = !isWsConnected ? <ConnectionBanner /> : null;

  const renderConnectorContent = () => {
    if (!chosenConnector) return null;

    // Whose charge is this? `myConnectorIds` is the driver's own live bays at
    // this station, from the last snapshot unioned with the session on screen.
    // A busy bay that is not in it belongs to somebody else, and the live view
    // — its progress, its Stop button — is not ours to render. The picker
    // already knows this rule; the screen reached by its own address did not.
    const isOwnCharge =
      myConnectorIds.includes(chosenConnector.connectorId) ||
      startedBayRef.current === chosenConnector.connectorId;
    const isBusy =
      chosenConnector.status === ConnectorStatus.Charging ||
      chosenConnector.status === ConnectorStatus.Suspended_EV ||
      chosenConnector.status === ConnectorStatus.Finishing;

    if (isBusy && !isOwnCharge) {
      return <ConnectorInUse connectorId={chosenConnector.connectorId} />;
    }

    switch (chosenConnector.status) {
      case ConnectorStatus.Available:
        return <InsertTheConnector connectorTypes={station?.connectorTypes} />;
      // SuspendedEV: the car stopped drawing but the transaction is still
      // open — same live view (Stop ends it), plus the idle tracker once the
      // backend has started the clock.
      case ConnectorStatus.Charging:
      case ConnectorStatus.Suspended_EV:
        return (
          <Box sx={{ mt: 4, width: '100%' }}>
            <ChargingBar
              percent={progress}
              chargedKwh={chargedEnergyKwh}
              powerKw={chargingMeta.powerKw}
              startedAt={chargingMeta.startedAt}
            />
            {isIdleWindow && (
              <Box sx={{ mt: 3 }}>
                <IdleStatus preview={idlePreview} />
              </Box>
            )}
            <Stack sx={{ marginTop: '20px', width: '100%' }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={stopTransaction}
              >
                {t('orderPage.stop')}
              </Button>
              {/* The only error surface on this branch: StationButton (which
                  carries `chargingError` everywhere else) renders nothing
                  while charging, so a failed stop would otherwise be silent. */}
              {chargingError ? (
                <Typography
                  variant="caption"
                  color="error"
                  textAlign="center"
                  sx={{ mt: 1 }}
                >
                  {chargingError}
                </Typography>
              ) : null}
            </Stack>
            <Box sx={{ mt: 2 }}>
              <TelegramNotifyPrompt />
            </Box>
          </Box>
        );
      case ConnectorStatus.Finishing:
        return (
          <Box sx={{ mt: 4, width: '100%' }}>
            <ChargingBar
              percent={progress}
              chargedKwh={chargedEnergyKwh}
              complete
            />
            {isIdleWindow && (
              <Box sx={{ mt: 3 }}>
                <IdleStatus preview={idlePreview} />
              </Box>
            )}
          </Box>
        );
      default:
        return (
          <>
            <InputValueBlock
              id={id!}
              setToPay={setToPay}
              inputValue={inputValue}
              setInputValue={setInputValue}
              connectorStatus={chosenConnector.status as ConnectorStatus}
              connectorId={chosenConnector.connectorId}
            />
            {toPay && <PayAmountBlock toPay={toPay} />}
          </>
        );
    }
  };

  const hasAvailableConnector = station?.connectors?.some(
    (c) =>
      c.status === ConnectorStatus.Available ||
      c.status === ConnectorStatus.Preparing,
  );
  if (!activeChargingSession && station?.status === StationStatus.In_use && !hasAvailableConnector)
    return <StationInUse />;

  if (isUserPhoneConfirmed !== null && !isUserPhoneConfirmed)
    return <Navigate to="/cabinet" />;

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        // Tightened from 25px: the gap is spent between every row, so it was
        // the largest single claim on a short phone's vertical budget — and the
        // car illustration at the bottom is what paid for it.
        gap: { xs: '20px' },
        maxWidth: '400px',
        width: '100%',
        padding: { xs: '12px 22px 20px' },
        alignSelf: 'center',
      }}
    >
      <StationPageHeader
        address={stationAddress}
        onBack={
          canChangeConnector
            ? () => {
                setChosenConnector(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            : undefined
        }
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />
      {reconnectingBanner}
      {chosenConnector ? (
        <>
          <ChosenConnector
            connectorId={chosenConnector.connectorId}
            connectorStatus={chosenConnector.status}
            stationPrice={station?.price as number}
            downtimePricePerMinute={station?.downtimePricePerMinute}
            downtimeFreeMinutes={station?.downtimeFreeMinutes}
            premiumPricePerKwh={userPremiumPrice}
          />
          {renderConnectorContent()}
          {chosenConnector.status !== ConnectorStatus.Charging &&
            chosenConnector.status !== ConnectorStatus.Suspended_EV &&
            chosenConnector.status !== ConnectorStatus.Finishing &&
            chosenConnector.status !== ConnectorStatus.Available && (
              <>
                {toPay && toPay.amount > 0 && (
                  <Typography
                    variant="body2"
                    color="common.white"
                    textAlign="center"
                    sx={{ opacity: 0.8, fontSize: '12px' }}
                  >
                    {t('orderPage.balanceTopUpHint')}
                  </Typography>
                )}
                <StationButton
                  chosenConnector={chosenConnector}
                  activeChargingSession={activeChargingSession}
                  chargingError={chargingError}
                  disabled={checkDisableButton()}
                  error={chargingError}
                  onClick={onButtonClick}
                  text={
                    activeChargingSession &&
                    +activeChargingSession.connectorId ===
                      chosenConnector?.connectorId
                      ? t('orderPage.continue')
                      : toPay?.amount === 0
                        ? t('orderPage.start')
                        : t('orderPage.pay')
                  }
                />
              </>
            )}
        </>
      ) : chosenConnector === null ? (
        station && usableConnectors(station.connectors).length > 1 ? (
          <ConnectorPicker
            connectors={usableConnectors(station.connectors)}
            stationPrice={station.price}
            userActiveConnectorIds={myConnectorIds}
            premiumPricePerKwh={userPremiumPrice}
            onSelect={(connector) => {
              selectConnector(connector);
              window.scrollTo({ top: 0, behavior: 'smooth' });
              navigate(
                `/${ROUTES.CABINET}/${ROUTES.CAB_STATION}/${id}/${connector.connectorId}`,
                { replace: true },
              );
            }}
          />
        ) : (
          <InsertTheConnector connectorTypes={station?.connectorTypes} />
        )
      ) : null}
    </Stack>
  );
};
